import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import {
  LuSearch, LuSend, LuMessageSquare, LuRefreshCw, LuImage, LuPlay, LuPause,
  LuShoppingCart, LuMic, LuTrash2, LuCircleStop,
} from 'react-icons/lu';
import Loading from '../../../shared/components/Loading/Loading';
import { storage } from '../../../config/firebase';
import { fetchConversations, fetchMessages, sendMessage } from './atendimentoService';
import './AtendimentoPage.css';

function formatDuration(seconds) {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function VoiceMessagePlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) audio.pause();
    else audio.play();
  }

  function handleSeek(e) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const ratio = e.target.value / 100;
    audio.currentTime = ratio * duration;
  }

  return (
    <div className="at-voice-player">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => {
          setCurrentTime(e.target.currentTime);
          setProgress(duration ? (e.target.currentTime / duration) * 100 : 0);
        }}
      />
      <button type="button" className="at-voice-play-btn" onClick={togglePlay}>
        {playing ? <LuPause size={14} /> : <LuPlay size={14} />}
      </button>
      <input
        type="range"
        className="at-voice-seek"
        min="0"
        max="100"
        value={progress || 0}
        onChange={handleSeek}
      />
      <span className="at-voice-time">{formatDuration(playing || currentTime ? currentTime : duration)}</span>
    </div>
  );
}

// Fixa o fuso em Brasília — a loja é só no Brasil, e sem isso o horário
// exibido dependia do relógio/fuso configurado no computador de quem
// estivesse olhando a tela, podendo mostrar tudo errado.
const TIMEZONE = 'America/Sao_Paulo';

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toLocaleDateString('pt-BR', { timeZone: TIMEZONE })
    === now.toLocaleDateString('pt-BR', { timeZone: TIMEZONE });
  if (isToday) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: TIMEZONE });
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: TIMEZONE });
}

function formatFullTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: TIMEZONE,
  });
}

// Status da Twilio pro lado do atendente — principalmente pra avisar quando
// uma mensagem não chega (ex: fora da janela de 24h do WhatsApp), que antes
// não tinha nenhum indicativo visual e passava despercebido.
function formatMessageStatus(msg) {
  if (msg.direction !== 'outbound') return null;
  switch (msg.status) {
    case 'sending':
      return { label: 'enviando…', failed: false };
    case 'failed':
    case 'undelivered':
      return { label: 'não entregue', failed: true };
    case 'read':
      return { label: 'lido', failed: false };
    case 'delivered':
      return { label: 'entregue', failed: false };
    case 'sent':
    case 'queued':
    case 'accepted':
      return { label: 'enviado', failed: false };
    default:
      return null;
  }
}

function formatPhone(phone) {
  const d = phone.replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  if (d.length === 12) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`;
  return phone;
}

const PAGE_SIZE = 100;

const AVATAR_COLORS = [
  '#E53935','#8E24AA','#3949AB','#039BE5',
  '#00897B','#43A047','#FB8C00','#6D4C41',
  '#1E88E5','#D81B60','#00ACC1','#7CB342',
];

function getInitials(name, phone) {
  if (name) {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  }
  if (phone) {
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length >= 2) return digits.slice(-2);
    if (digits.length === 1) return digits;
  }
  return '?';
}

function getAvatarColor(str) {
  if (!str) return AVATAR_COLORS[0];
  let hash = 0;
  const s = String(str);
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const RECORDER_MIME_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];

function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  return RECORDER_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

export default function AtendimentoPage() {
  const [conversations, setConversations] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedAudio, setRecordedAudio] = useState(null); // { blob, mimeType, url } — aguardando decisão
  const bottomRef = useRef(null);
  const justOpenedRef = useRef(false);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const activeRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const streamRef = useRef(null);

  activeRef.current = active;

  function mergeConversations(prev, incoming) {
    const map = new Map(prev.map((c) => [c.phone, c]));
    incoming.forEach((c) => map.set(c.phone, c));
    // Não lidas sempre no topo, igual ao WhatsApp Web; dentro do grupo, mais recente primeiro.
    return Array.from(map.values()).sort((a, b) => {
      if (!!a.unread !== !!b.unread) return a.unread ? -1 : 1;
      return new Date(b.lastMessageDate) - new Date(a.lastMessageDate);
    });
  }

  // Recarrega só a 1ª página (as conversas mais recentes) — preserva páginas
  // extras já carregadas via "carregar mais" em vez de descartá-las.
  const loadConversations = useCallback(async (silent = false) => {
    if (!silent) setLoadingConvs(true);
    try {
      const { conversations: page1, total } = await fetchConversations(1, PAGE_SIZE);
      setConversations((prev) => {
        const merged = mergeConversations(prev, page1);
        setHasMore(merged.length < total);
        return merged;
      });
    } catch {
      if (!silent) toast.error('Erro ao carregar conversas.');
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  const loadMoreConversations = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = Math.floor(conversations.length / PAGE_SIZE) + 1;
      const { conversations: more, total } = await fetchConversations(nextPage, PAGE_SIZE);
      setConversations((prev) => {
        const merged = mergeConversations(prev, more);
        setHasMore(merged.length < total);
        return merged;
      });
    } catch {
      toast.error('Erro ao carregar mais conversas.');
    } finally {
      setLoadingMore(false);
    }
  }, [conversations.length, hasMore, loadingMore]);

  // Scroll infinito — carrega mais conversas ao chegar perto do final da
  // lista, igual ao WhatsApp Web (sem busca ativa, que já filtra localmente).
  function handleConvListScroll(e) {
    if (search) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      loadMoreConversations();
    }
  }

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      q
        ? conversations.filter(
            (c) =>
              c.phone?.includes(q) ||
              c.lastMessage?.toLowerCase().includes(q) ||
              c.name?.toLowerCase().includes(q)
          )
        : conversations
    );
  }, [search, conversations]);

  const loadMessages = useCallback(async (phone, silent = false) => {
    if (!silent) setLoadingMsgs(true);
    try {
      const data = await fetchMessages(phone);
      setMessages(data);
    } catch {
      if (!silent) toast.error('Erro ao carregar mensagens.');
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  // Polling de conversas a cada 3s para refletir mensagens novas
  useEffect(() => {
    const id = setInterval(() => loadConversations(true), 3000);
    return () => clearInterval(id);
  }, [loadConversations]);

  useEffect(() => {
    if (!active) return;
    justOpenedRef.current = true;
    loadMessages(active.phone);
    pollRef.current = setInterval(() => {
      if (activeRef.current) loadMessages(activeRef.current.phone, true);
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [active, loadMessages]);

  // Ao abrir uma conversa, pula direto pro final (sem animação). Só rola de
  // novo quando chega mensagem nova de verdade — o polling reseta `messages`
  // a cada 2s mesmo sem nada novo, e sem essa checagem isso puxava o scroll
  // pra baixo toda hora, impedindo o usuário de subir pra ler o histórico.
  const lastMessageSidRef = useRef(null);
  useEffect(() => {
    const lastSid = messages[messages.length - 1]?.sid ?? null;
    const isNew = lastSid !== lastMessageSidRef.current;
    lastMessageSidRef.current = lastSid;
    if (justOpenedRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'auto' });
      justOpenedRef.current = false;
    } else if (isNew) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  function openConversation(conv) {
    if (sending || uploadingMedia) return;
    clearInterval(pollRef.current);
    setActive(conv);
    setMessages([]);
    setInput('');
  }

  async function handleSend(e) {
    e?.preventDefault();
    if (!input.trim() || !active || sending) return;
    const body = input.trim();
    setInput('');

    // Mensagem otimista — aparece na hora
    const optimistic = {
      sid: `opt_${Date.now()}`,
      body,
      direction: 'outbound',
      date: new Date().toISOString(),
      status: 'sending',
      numMedia: 0,
    };
    setMessages((prev) => [...prev, optimistic]);

    setSending(true);
    try {
      await sendMessage({ phone: active.phone, body });
      // Confirma com dados reais
      await loadMessages(active.phone, true);
    } catch {
      toast.error('Erro ao enviar. A janela de 24h pode ter expirado.');
      setMessages((prev) => prev.filter((m) => m.sid !== optimistic.sid));
      setInput(body);
    } finally {
      setSending(false);
    }
  }

  async function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file || !active) return;
    e.target.value = '';

    setUploadingMedia(true);
    try {
      const ext = file.name.split('.').pop();
      const ref = storage.ref(`whatsapp-media/${Date.now()}.${ext}`);
      await ref.put(file);
      const mediaUrl = await ref.getDownloadURL();
      const mediaContentType = file.type;

      // Otimista para mídia
      const optimistic = {
        sid: `opt_img_${Date.now()}`,
        body: '',
        direction: 'outbound',
        date: new Date().toISOString(),
        status: 'sending',
        numMedia: 1,
        mediaUrl,
        mediaContentType,
      };
      setMessages((prev) => [...prev, optimistic]);

      await sendMessage({ phone: active.phone, mediaUrl, mediaContentType });
      await loadMessages(active.phone, true);
    } catch {
      toast.error('Erro ao enviar mídia.');
    } finally {
      setUploadingMedia(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) handleSend(e);
  }

  async function sendVoiceMessage(blob, mimeType) {
    if (!active) return;
    setUploadingMedia(true);
    try {
      const ext = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm';
      const ref = storage.ref(`whatsapp-media/${Date.now()}.${ext}`);
      await ref.put(blob, { contentType: mimeType });
      const mediaUrl = await ref.getDownloadURL();

      const optimistic = {
        sid: `opt_audio_${Date.now()}`,
        body: '',
        direction: 'outbound',
        date: new Date().toISOString(),
        status: 'sending',
        numMedia: 1,
        mediaUrl,
        mediaContentType: mimeType,
      };
      setMessages((prev) => [...prev, optimistic]);

      await sendMessage({ phone: active.phone, mediaUrl, mediaContentType: mimeType });
      await loadMessages(active.phone, true);
    } catch {
      toast.error('Erro ao enviar áudio.');
    } finally {
      setUploadingMedia(false);
    }
  }

  function stopMicStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  async function startRecording() {
    if (recording || !active) return;
    const mimeType = pickRecorderMimeType();
    if (mimeType === null) {
      toast.error('Gravação de áudio não é suportada neste navegador.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recordedChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordingTime(0);
      setRecording(true);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);
    } catch {
      toast.error('Não foi possível acessar o microfone.');
      stopMicStream();
    }
  }

  // Para a captura e guarda o áudio pra revisão — quem decide se descarta ou
  // envia é o botão de lixeira/enviar da barra de revisão, não o stop em si.
  function stopRecordingCapture() {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    clearInterval(recordingTimerRef.current);
    recorder.onstop = () => {
      stopMicStream();
      if (recordedChunksRef.current.length) {
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setRecordedAudio({ blob, mimeType, url });
      }
      recordedChunksRef.current = [];
      mediaRecorderRef.current = null;
    };
    recorder.stop();
    setRecording(false);
  }

  function discardRecordedAudio() {
    if (recordedAudio?.url) URL.revokeObjectURL(recordedAudio.url);
    setRecordedAudio(null);
    setRecordingTime(0);
  }

  async function confirmSendRecordedAudio() {
    if (!recordedAudio) return;
    const { blob, mimeType, url } = recordedAudio;
    URL.revokeObjectURL(url);
    setRecordedAudio(null);
    setRecordingTime(0);
    await sendVoiceMessage(blob, mimeType);
  }

  useEffect(() => () => {
    clearInterval(recordingTimerRef.current);
    stopMicStream();
    if (recordedAudio?.url) URL.revokeObjectURL(recordedAudio.url);
  }, [recordedAudio]);

  return (
    <div className="at-page">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* PAINEL ESQUERDO */}
      <div className="at-left">
        <div className="at-left-header">
          <div className="at-left-title">
            <LuMessageSquare size={16} />
            Conversas
          </div>
          <button className="at-refresh-btn" onClick={() => loadConversations()} title="Atualizar">
            <LuRefreshCw size={14} />
          </button>
        </div>

        <div className="at-search-wrap">
          <div className="at-search-inner">
            <LuSearch size={15} className="at-search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome ou número..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="at-search-input"
            />
          </div>
        </div>

        <div
          className={`at-conv-list ${(sending || uploadingMedia) ? 'at-conv-list--locked' : ''}`}
          onScroll={handleConvListScroll}
        >
          {loadingConvs ? (
            <div className="at-center-loader"><Loading /></div>
          ) : filtered.length === 0 ? (
            <div className="at-empty">Nenhuma conversa encontrada.</div>
          ) : (
            filtered.map((conv) => (
              <button
                key={conv.phone}
                className={`at-conv-item ${active?.phone === conv.phone ? 'at-conv-item--on' : ''} ${conv.unread ? 'at-conv-item--unread' : ''}`}
                onClick={() => openConversation(conv)}
                disabled={sending || uploadingMedia}
              >
                <div className="at-conv-avatar" style={{ background: getAvatarColor(conv.phone) }}>
                  {getInitials(conv.name, conv.phone)}
                </div>
                <div className="at-conv-info">
                  <div className="at-conv-top">
                    <span className="at-conv-phone">{conv.name || formatPhone(conv.phone)}</span>
                    <div className="at-conv-meta">
                      <span className="at-conv-time">{formatTime(conv.lastMessageDate)}</span>
                      {conv.unread && <span className="at-conv-unread-dot" />}
                    </div>
                  </div>
                  <div className={`at-conv-preview ${conv.direction === 'outbound' ? 'at-conv-preview--out' : ''}`}>
                    {conv.direction === 'outbound' && <span className="at-conv-you">Você: </span>}
                    {conv.lastMessage?.slice(0, 55)}{conv.lastMessage?.length > 55 ? '…' : ''}
                  </div>
                </div>
              </button>
            ))
          )}
          {!search && loadingMore && (
            <div className="at-conv-list-loading-more"><Loading size="sm" /></div>
          )}
        </div>
      </div>

      {/* PAINEL DIREITO */}
      <div className="at-right">
        {!active ? (
          <div className="at-empty-state">
            <LuMessageSquare size={48} strokeWidth={1} />
            <p>Selecione uma conversa para começar</p>
          </div>
        ) : (
          <>
            <div className="at-chat-header">
              <div className="at-conv-avatar at-chat-avatar" style={{ background: getAvatarColor(active.phone) }}>
                {getInitials(active.name, active.phone)}
              </div>
              <div>
                <div className="at-chat-name">{active.name || formatPhone(active.phone)}</div>
                <div className="at-chat-sub">{formatPhone(active.phone)}</div>
              </div>
            </div>

            <div className="at-messages">
              {loadingMsgs ? (
                <div className="at-center-loader"><Loading /></div>
              ) : messages.length === 0 ? (
                <div className="at-empty">Nenhuma mensagem ainda.</div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.sid}
                    className={`at-bubble-wrap ${msg.direction === 'outbound' ? 'at-bubble-wrap--out' : ''}`}
                  >
                    <div className={`at-bubble ${msg.direction === 'outbound' ? 'at-bubble--out' : 'at-bubble--in'}`}>
                      {msg.orderItems ? (
                        <div className="at-order-card">
                          <div className="at-order-header">
                            <LuShoppingCart size={14} />
                            <span>Pedido do catálogo</span>
                          </div>
                          {msg.orderItems.map((item, i) => (
                            <div key={i} className="at-order-item">
                              {item.productImage && (
                                <img src={item.productImage} alt="" className="at-order-item-img" />
                              )}
                              <div className="at-order-item-info">
                                <span className="at-order-item-name">
                                  {item.productName || item.productRetailerId}
                                </span>
                                <span className="at-order-item-qty">
                                  {item.quantity}x {item.currency} {item.itemPrice?.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ))}
                          <div className="at-order-total">
                            Total: {msg.orderItems[0]?.currency}{' '}
                            {msg.orderItems
                              .reduce((sum, i) => sum + (i.quantity * i.itemPrice || 0), 0)
                              .toFixed(2)}
                          </div>
                        </div>
                      ) : msg.mediaUrl && msg.mediaContentType?.startsWith('audio/') ? (
                        <VoiceMessagePlayer src={msg.mediaUrl} />
                      ) : msg.mediaUrl && msg.mediaContentType?.startsWith('video/') ? (
                        <video src={msg.mediaUrl} controls className="at-bubble-img" />
                      ) : msg.mediaUrl ? (
                        <img
                          src={msg.mediaUrl}
                          alt="mídia"
                          className="at-bubble-img"
                          onClick={() => window.open(msg.mediaUrl, '_blank')}
                        />
                      ) : msg.numMedia > 0 ? (
                        <span className="at-bubble-media">📎 Mídia recebida</span>
                      ) : msg.body ? (
                        <span className="at-bubble-body">{msg.body}</span>
                      ) : (
                        <span className="at-bubble-media">
                          🛒 Mensagem sem suporte — confira direto no WhatsApp
                        </span>
                      )}
                      <span className="at-bubble-time">
                        {formatFullTime(msg.date)}
                        {(() => {
                          const status = formatMessageStatus(msg);
                          if (!status) return null;
                          return (
                            <span className={status.failed ? 'at-bubble-status--failed' : undefined}>
                              {' · '}{status.label}
                            </span>
                          );
                        })()}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={bottomRef} />
            </div>

            {recording ? (
              <div className="at-recording-bar">
                <button
                  type="button"
                  className="at-recording-cancel"
                  onClick={discardRecordedAudio}
                  title="Cancelar gravação"
                >
                  <LuTrash2 size={18} />
                </button>
                <span className="at-recording-dot" />
                <span className="at-recording-time">{formatDuration(recordingTime)}</span>
                <span className="at-recording-hint">Gravando áudio…</span>
                <button
                  type="button"
                  className="at-recording-stop"
                  onClick={stopRecordingCapture}
                  title="Parar gravação"
                >
                  <LuCircleStop size={20} />
                </button>
              </div>
            ) : recordedAudio ? (
              <div className="at-recording-bar">
                <button
                  type="button"
                  className="at-recording-cancel"
                  onClick={discardRecordedAudio}
                  disabled={uploadingMedia}
                  title="Excluir gravação"
                >
                  <LuTrash2 size={18} />
                </button>
                <VoiceMessagePlayer src={recordedAudio.url} />
                <button
                  type="button"
                  className="at-send-btn"
                  onClick={confirmSendRecordedAudio}
                  disabled={uploadingMedia}
                  title="Enviar áudio"
                >
                  {uploadingMedia ? <Loading size="sm" /> : <LuSend size={16} />}
                </button>
              </div>
            ) : (
              <form className="at-input-bar" onSubmit={handleSend}>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/*,audio/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <button
                  type="button"
                  className="at-media-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingMedia}
                  title="Enviar imagem"
                >
                  {uploadingMedia ? <Loading size="sm" /> : <LuImage size={18} />}
                </button>
                <textarea
                  className="at-input"
                  placeholder="Digite uma mensagem..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                />
                {input.trim() || sending ? (
                  <button
                    type="submit"
                    className="at-send-btn"
                    disabled={sending}
                  >
                    {sending ? <Loading size="sm" /> : <LuSend size={16} />}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="at-mic-btn"
                    onClick={startRecording}
                    disabled={uploadingMedia}
                    title="Gravar áudio"
                  >
                    <LuMic size={18} />
                  </button>
                )}
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
